import {
  Building2,
  User,
  Users,
  FileQuestion,
  Clock,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Send,
} from 'lucide-react';

// Wizard step configuration
export const WIZARD_STEPS = [
  { id: 1, key: 'group', label: 'Supplier Group', icon: Building2 },
  { id: 2, key: 'details', label: 'Supplier Details', icon: Building2 },
  { id: 3, key: 'profile', label: 'Supplier User Profile', icon: User },
  { id: 4, key: 'companies', label: 'Supplier User Related Companies', icon: Users },
  { id: 5, key: 'risk', label: 'Risk Questions', icon: FileQuestion },
];

// Status configuration (string-based, matching SupplierProfile.onboarding_status)
export const STATUS_LABELS: Record<string, { label: string; color: string; icon: React.ElementType }> = {
  draft: { label: 'Draft', color: 'bg-blue-100 text-blue-800', icon: Clock },
  pending_initial: { label: 'Pending Initial Approval', color: 'bg-yellow-100 text-yellow-800', icon: AlertTriangle },
  invited: { label: 'Invited', color: 'bg-gray-100 text-gray-800', icon: Send },
  not_approved: { label: 'Not Approved', color: 'bg-red-100 text-red-800', icon: XCircle },
  due_diligence: { label: 'Pending Approval (Due Diligence)', color: 'bg-indigo-100 text-indigo-800', icon: FileQuestion },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
};

// CR-4147: Expanded currency options
export const CURRENCY_OPTIONS = [
  { value: 'GBP', label: 'GBP - British Pound' },
  { value: 'USD', label: 'USD - US Dollar' },
  { value: 'EUR', label: 'EUR - Euro' },
  { value: 'INR', label: 'INR - Indian Rupee' },
  { value: 'JPY', label: 'JPY - Japanese Yen' },
  { value: 'CHF', label: 'CHF - Swiss Franc' },
  { value: 'AUD', label: 'AUD - Australian Dollar' },
  { value: 'CAD', label: 'CAD - Canadian Dollar' },
  { value: 'CNY', label: 'CNY - Chinese Yuan' },
  { value: 'SEK', label: 'SEK - Swedish Krona' },
  { value: 'NOK', label: 'NOK - Norwegian Krone' },
  { value: 'DKK', label: 'DKK - Danish Krone' },
  { value: 'SGD', label: 'SGD - Singapore Dollar' },
  { value: 'HKD', label: 'HKD - Hong Kong Dollar' },
  { value: 'KRW', label: 'KRW - South Korean Won' },
  { value: 'ZAR', label: 'ZAR - South African Rand' },
  { value: 'BRL', label: 'BRL - Brazilian Real' },
  { value: 'MXN', label: 'MXN - Mexican Peso' },
  { value: 'PLN', label: 'PLN - Polish Zloty' },
  { value: 'THB', label: 'THB - Thai Baht' },
  { value: 'MYR', label: 'MYR - Malaysian Ringgit' },
  { value: 'NZD', label: 'NZD - New Zealand Dollar' },
  { value: 'AED', label: 'AED - UAE Dirham' },
  { value: 'SAR', label: 'SAR - Saudi Riyal' },
  { value: 'TRY', label: 'TRY - Turkish Lira' },
];

// CR-4144: Currency symbol mapping for annual spend labels
export const CURRENCY_SYMBOLS: Record<string, string> = {
  GBP: '£', USD: '$', EUR: '€', INR: '₹', JPY: '¥', CHF: 'CHF ',
  AUD: 'A$', CAD: 'C$', CNY: '¥', SEK: 'kr ', NOK: 'kr ', DKK: 'kr ',
  SGD: 'S$', HKD: 'HK$', KRW: '₩', ZAR: 'R', BRL: 'R$', MXN: 'MX$',
  PLN: 'zł', THB: '฿', MYR: 'RM ', NZD: 'NZ$', AED: 'AED ', SAR: 'SAR ',
  TRY: '₺',
};

// CR-4144: Dynamic annual spend options based on selected currency
export function getAnnualSpendOptions(currency: string): { value: string; label: string }[] {
  const symbol = CURRENCY_SYMBOLS[currency] || '£';
  return [
    { value: 'under_10k', label: `Under ${symbol}10,000` },
    { value: '10k_50k', label: `${symbol}10,000 - ${symbol}50,000` },
    { value: '50k_100k', label: `${symbol}50,000 - ${symbol}100,000` },
    { value: '100k_500k', label: `${symbol}100,000 - ${symbol}500,000` },
    { value: '500k_1m', label: `${symbol}500,000 - ${symbol}1,000,000` },
    { value: 'over_1m', label: `Over ${symbol}1,000,000` },
  ];
}

// Department options (CR-3952)
export const DEPARTMENT_OPTIONS = [
  { value: 'development', label: 'Development' },
  { value: 'quality', label: 'Quality' },
  { value: 'site_supply_chain', label: 'Company Supply Chain' },
];

// Login type options (CR-3951)
export const LOGIN_TYPE_OPTIONS = [
  { value: 'supplier', label: 'Supplier' },
  { value: 'customer', label: 'Customer' },
];

// Title options (matching Valuechain)
export const TITLE_OPTIONS = [
  { value: 'Mr', label: 'Mr' },
  { value: 'Mrs', label: 'Mrs' },
  { value: 'Ms', label: 'Ms' },
  { value: 'Miss', label: 'Miss' },
  { value: 'Dr', label: 'Dr' },
  { value: 'Prof', label: 'Prof' },
];

// Contact entry type
export interface ContactEntry {
  title: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  phone_extension: string;
  job_title: string;
  department: string;
  login_type: string;
  linkedin: string;
  is_primary: boolean;
}

// Site entry type (CR-3944)
export interface SiteEntry {
  name: string;
  address: string;
  country: string;
}

// Form data type
export interface SupplierFormData {
  // Supplier Group
  logo_url?: string;
  supplier_group_id?: number;
  supplier_group_name?: string;
  is_part_of_group: boolean | null; // CR-3942

  // CR-6696: Primary Company for the supplier user (group suppliers only).
  // 0 = the new supplier being onboarded (it has no real id until created);
  // any other value is a sibling company id from the selected supplier group.
  primary_company_id?: number | null;
  primary_company_name?: string;

  // Supplier Details
  customer_id?: number;
  customer_name?: string;
  customer_ids: number[];
  // CR-6366 alignment: per-customer supplier code + trading currency, keyed by
  // customer id. Each linked company keeps its own values, written to that
  // company's relationship. Blank entries fall back to the single top-level
  // currency (which stays tied to the primary site for Annual Spend).
  customer_settings?: Record<number, { supplier_code: string; currency: string }>;

  // CR-5877: Risk Question Routing Management.
  // Primary customer is no longer inferred from selection order — the buyer
  // explicitly defines how risk questionnaires are routed across the linked
  // customer companies.
  //   level=group  → route to the entire customer group (routing_group_id)
  //   level=site   → route at site level; routing_site_scope is then either
  //                  'single' (one specific site via routing_site_id) or
  //                  'all'    (every linked site in routing_group_id)
  risk_routing_level?: 'group' | 'site' | null;
  risk_routing_site_scope?: 'single' | 'all' | null;
  risk_routing_group_id?: number | null;
  risk_routing_site_id?: number | null;
  company_name: string;
  company_address: string;
  // CR-5390: structured address. Postcode + Address Line 1 are mandatory.
  // Country defaults follow Company model semantics.
  address_line_1: string;
  address_line_2: string;
  address_city: string;
  address_region: string;
  address_postcode: string;
  address_country: string;
  /** The supplier organisation's own email — distinct from contact_email, which
   *  belongs to the primary contact person. Syncs to Company.primary_email. */
  company_email: string;
  currency: string;
  annual_spend: string;
  description: string;
  sites: SiteEntry[]; // CR-3944

  // User Profile (CR-3950: split name into first/last)
  // CR-4148: First contact is always the primary contact
  contact_title: string;
  contact_first_name: string;
  contact_last_name: string;
  contact_email: string;
  contact_phone: string;
  contact_phone_extension: string;
  contact_job_title: string;
  contact_department: string; // CR-3952
  contact_login_type: string; // CR-3951
  contact_linkedin: string;
  additional_contacts: ContactEntry[]; // CR-3949

  // Related Companies
  related_companies: Array<{
    name: string;
    address: string;
    type: string;
  }>;

  // Site Access Configuration (Step 4)
  site_access?: Record<string, SiteAccessConfig>;

  // Risk Questions
  risk_answers: Record<string, string>;
}

/** Per-contact-per-site access configuration */
export interface SiteAccessConfig {
  active?: boolean;
  modules?: Record<string, boolean>;
  role?: string;
  notifications?: Record<string, boolean>;
}

// Default risk questions shown when no dataset is configured for the customer
// Each option has a score: higher score = higher risk
export const DEFAULT_RISK_QUESTIONS: Array<{
  id: string;
  question: string;
  type: string;
  options: string[];
  scores?: Record<string, number>;  // option label → score (0 = no risk, 10 = max risk)
  max_score?: number;
}> = [
  {
    id: 'default_1',
    question: 'Does the supplier have a documented quality management system (e.g., ISO 9001)?',
    type: 'single_choice',
    options: ['Yes - Certified', 'Yes - Not Certified', 'In Progress', 'No'],
    scores: { 'Yes - Certified': 0, 'Yes - Not Certified': 3, 'In Progress': 6, 'No': 10 },
    max_score: 10,
  },
  {
    id: 'default_2',
    question: 'Does the supplier have appropriate insurance coverage?',
    type: 'single_choice',
    options: ['Yes - Fully Covered', 'Yes - Partial Coverage', 'No'],
    scores: { 'Yes - Fully Covered': 0, 'Yes - Partial Coverage': 5, 'No': 10 },
    max_score: 10,
  },
  {
    id: 'default_3',
    question: 'Does the supplier have a documented environmental management system?',
    type: 'single_choice',
    options: ['Yes - ISO 14001 Certified', 'Yes - Other Standard', 'In Progress', 'No'],
    scores: { 'Yes - ISO 14001 Certified': 0, 'Yes - Other Standard': 2, 'In Progress': 5, 'No': 10 },
    max_score: 10,
  },
  {
    id: 'default_4',
    question: 'Does the supplier have a business continuity plan?',
    type: 'single_choice',
    options: ['Yes - Tested', 'Yes - Untested', 'In Progress', 'No'],
    scores: { 'Yes - Tested': 0, 'Yes - Untested': 3, 'In Progress': 6, 'No': 10 },
    max_score: 10,
  },
  {
    id: 'default_5',
    question: 'Does the supplier comply with modern slavery legislation?',
    type: 'single_choice',
    options: ['Yes - Published Statement', 'Yes - No Statement', 'Not Applicable', 'No'],
    scores: { 'Yes - Published Statement': 0, 'Yes - No Statement': 4, 'Not Applicable': 2, 'No': 10 },
    max_score: 10,
  },
  {
    id: 'default_6',
    question: 'What is the financial stability rating of the supplier?',
    type: 'single_choice',
    options: ['Strong', 'Adequate', 'Weak', 'Unknown'],
    scores: { 'Strong': 0, 'Adequate': 3, 'Weak': 8, 'Unknown': 10 },
    max_score: 10,
  },
  {
    id: 'default_7',
    question: 'Does the supplier have relevant industry certifications?',
    type: 'single_choice',
    options: ['Yes - Multiple', 'Yes - One', 'In Progress', 'No'],
    scores: { 'Yes - Multiple': 0, 'Yes - One': 2, 'In Progress': 5, 'No': 10 },
    max_score: 10,
  },
  {
    id: 'default_8',
    question: 'Please provide any additional notes or concerns about this supplier.',
    type: 'text',
    options: [],
  },
];

// Decline reason options
export const DECLINE_REASONS = [
  'Incomplete documentation',
  'Failed compliance check',
  'Financial concerns',
  'Capacity issues',
  'Quality concerns',
  'Other',
];

// Approval levels
export const APPROVAL_LEVELS = [
  { id: 1, label: 'Level 1 - Basic Approval' },
  { id: 2, label: 'Level 2 - Standard Approval' },
  { id: 3, label: 'Level 3 - Full Approval' },
];

/**
 * Validate a specific wizard step's mandatory fields.
 * @param step - The step number to validate (1-5)
 * @param formData - Current wizard form data
 * @returns Error message string, or null if valid
 */
export function validateWizardStep(step: number, formData: SupplierFormData): string | null {
  if (step === 1) {
    if (formData.is_part_of_group === null) {
      return 'Please select whether this supplier is part of a group.';
    }
    if (formData.is_part_of_group === true && !formData.supplier_group_id) {
      return 'Please select or create a supplier group.';
    }
  }
  if (step === 2) {
    const missing: string[] = [];
    if (!formData.company_name?.trim()) missing.push('Supplier Name');
    // CR-5390: address is now structured; Address Line 1 + Postcode are required.
    if (!formData.address_line_1?.trim()) missing.push('Address Line 1');
    // CR-7774: City / Town is mandatory — suppliers were being saved with no
    // city, leaving incomplete addresses. ``trim`` so whitespace-only fails too.
    if (!formData.address_city?.trim()) missing.push('City / Town');
    if (!formData.address_postcode?.trim()) missing.push('Postcode');
    // CR-5876: Currency is mandatory for dashboard currency conversions.
    if (!formData.currency?.trim()) missing.push('Currency');
    if (!formData.annual_spend?.trim()) missing.push('Annual Spend');
    // CR-5390: customer selection consolidated into the Customer Companies section.
    if (!(formData.customer_ids?.length > 0)) missing.push('At least one Customer Company');
    if (missing.length > 0) {
      return `Please complete the following mandatory fields: ${missing.join(', ')}`;
    }
    // CR-5877: Risk question routing must be explicitly configured — no longer
    // inferred from selection order.
    if (!formData.risk_routing_level) {
      return 'Please choose how risk questionnaires should be routed (Group Level or Site Level).';
    }
    if (formData.risk_routing_level === 'group' && !formData.risk_routing_group_id) {
      return 'Please select the customer group for risk-question routing.';
    }
    if (formData.risk_routing_level === 'site') {
      if (!formData.risk_routing_site_scope) {
        return 'Please choose Single Site or All Sites for risk-question routing.';
      }
      if (formData.risk_routing_site_scope === 'single' && !formData.risk_routing_site_id) {
        return 'Please select the specific site for risk-question routing.';
      }
      if (formData.risk_routing_site_scope === 'all' && !formData.risk_routing_group_id) {
        return 'Please select the customer group whose sites will receive risk questionnaires.';
      }
    }
  }
  if (step === 3) {
    const missing: string[] = [];
    if (!formData.contact_first_name?.trim()) missing.push('First Name');
    if (!formData.contact_last_name?.trim()) missing.push('Last Name');
    if (!formData.contact_email?.trim()) missing.push('Email');
    if (missing.length > 0) {
      return `Please complete the following mandatory fields: ${missing.join(', ')}`;
    }
    for (let i = 0; i < formData.additional_contacts.length; i++) {
      const c = formData.additional_contacts[i];
      const cMissing: string[] = [];
      if (!c.first_name?.trim()) cMissing.push('First Name');
      if (!c.last_name?.trim()) cMissing.push('Last Name');
      if (!c.email?.trim()) cMissing.push('Email');
      if (cMissing.length > 0) {
        return `Additional Contact ${i + 1}: Please complete ${cMissing.join(', ')}`;
      }
    }
  }
  return null;
}
